import Tooltip, { TooltipTrigger } from '@orn/shared-web/components/Tooltip/Tooltip';
import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';

/**
 * MOPS-local dense operator table. Deliberately separate from
 * `@orn/shared-web/components/Table` — the shared-web primitive is styled for
 * SaaS dashboards (`h-14` rows, `rounded-2xl` card, `shadow-sm`) and cannot be
 * reconciled with the MOPS operator aesthetic documented in
 * `packages/mops-web/AGENTS.md` § Density + Visual chrome:
 *
 * - 28-32px row height
 * - zebra off, 1px borders over backgrounds
 * - sharp edges, no card chrome, no shadows
 * - `tabular-nums` for numeric alignment
 * - `font-mono` for IDs, tokens, emails, timestamps (opt-in via the `mono`
 *   prop on `Table.Cell`)
 * - sticky header (opt-in via the `sticky` prop on `Table.Header`)
 *
 * Uses native `<table>` / `<thead>` / `<tbody>` under the hood; the one
 * react-aria-components dependency is the tooltip a sortable header can carry,
 * whose trigger has to be the sort control itself. Sort chrome is the one
 * affordance it carries
 * (`Table.SortableHeaderCell`); row selection and keyboard grid navigation are
 * not needed for the admin surface. If they are later, extend this component
 * rather than reach for the shared-web primitive.
 */

interface ITableProps extends React.TableHTMLAttributes<HTMLTableElement> {
  children: React.ReactNode;
}

const TableRoot = ({ className, children, ...props }: ITableProps) => (
  <table className={cx('w-full border-collapse text-left text-sm tabular-nums', className)} {...props}>
    {children}
  </table>
);
TableRoot.displayName = 'Table';

interface ITableHeaderProps extends React.HTMLAttributes<HTMLTableSectionElement> {
  /** Pin the header to the top of its scroll container. */
  sticky?: boolean;
  children: React.ReactNode;
}

const TableHeader = ({ className, sticky = false, children, ...props }: ITableHeaderProps) => (
  <thead
    className={cx(
      'text-fg-secondary border-border-primary border-b text-xs font-medium uppercase tracking-wide',
      sticky && 'bg-bg-primary sticky top-0 z-10',
      className
    )}
    {...props}
  >
    {children}
  </thead>
);
TableHeader.displayName = 'Table.Header';

interface ITableBodyProps extends React.HTMLAttributes<HTMLTableSectionElement> {
  children: React.ReactNode;
}

const TableBody = ({ className, children, ...props }: ITableBodyProps) => (
  <tbody className={className} {...props}>
    {children}
  </tbody>
);
TableBody.displayName = 'Table.Body';

interface ITableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  children: React.ReactNode;
}

const TableRow = ({ className, children, ...props }: ITableRowProps) => (
  <tr
    className={cx('border-border-primary hover:bg-bg-secondary border-b transition-colors last:border-b-0', className)}
    {...props}
  >
    {children}
  </tr>
);
TableRow.displayName = 'Table.Row';

interface ITableHeaderCellProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  children?: React.ReactNode;
}

const TableHeaderCell = ({ className, children, ...props }: ITableHeaderCellProps) => (
  <th className={cx('px-3 py-2 font-medium', className)} {...props}>
    {children}
  </th>
);
TableHeaderCell.displayName = 'Table.HeaderCell';

export type TableSortDirection = 'asc' | 'desc';

interface IHeaderTooltip {
  title: string;
  description: string;
}

interface ITableSortableHeaderCellProps extends ITableHeaderCellProps {
  /** How this column orders the table right now, or null while another column does. */
  sortDirection: TableSortDirection | null;
  onSort: () => void;
  /** Hover/focus definition for a column whose label is too terse to stand alone. */
  tooltip?: IHeaderTooltip;
}

/**
 * Column title that orders the table on its column. The active column shows
 * its direction; the others show a faint two-way arrow so they read as
 * clickable rather than as plain labels. What a click does — flip, or take
 * over — is the caller's; this only reports the click.
 *
 * With `tooltip`, the control is a react-aria button so the definition can open
 * on hover and focus. Nesting a tooltip trigger inside a plain button would put
 * a button inside a button, so the two are the same element rather than
 * layered.
 */
const TableSortableHeaderCell = ({
  sortDirection,
  onSort,
  tooltip,
  className,
  children,
  ...props
}: ITableSortableHeaderCellProps) => {
  const active = sortDirection !== null;
  // Preflight resets a button's text-transform, so the header's case is re-inherited.
  const controlClassName = cx(
    'hover:text-fg-primary focus-visible:outline-fg-brand-primary block cursor-pointer whitespace-nowrap text-left [text-transform:inherit] focus-visible:outline focus-visible:outline-1',
    active && 'text-fg-primary'
  );
  const label = (
    <>
      {children}
      <span className={cx('ml-1', active ? 'text-fg-primary' : 'text-fg-quaternary')} aria-hidden>
        {sortDirection === null ? '↕' : sortDirection === 'asc' ? '▲' : '▼'}
      </span>
    </>
  );
  const title = active ? 'Flip the sort direction' : 'Sort by this column';

  return (
    <th
      className={cx('px-3 py-2 font-medium', className)}
      aria-sort={sortDirection === null ? 'none' : sortDirection === 'asc' ? 'ascending' : 'descending'}
      {...props}
    >
      {tooltip ? (
        <Tooltip title={tooltip.title} description={tooltip.description} placement="top" delay={150}>
          {/* No aria-label: the accessible name has to stay the column's own text, and the tooltip supplies the description. */}
          <TooltipTrigger onPress={onSort} className={controlClassName}>
            {label}
          </TooltipTrigger>
        </Tooltip>
      ) : (
        <button type="button" className={cx(controlClassName, 'w-full')} onClick={onSort} title={title}>
          {label}
        </button>
      )}
    </th>
  );
};
TableSortableHeaderCell.displayName = 'Table.SortableHeaderCell';

interface ITableCellProps extends React.TdHTMLAttributes<HTMLTableCellElement> {
  /** Render contents in monospace — use for IDs, tokens, emails, timestamps, JSON. */
  mono?: boolean;
  /** Mute the text color to `text-fg-secondary` — use for metadata columns. */
  muted?: boolean;
  children?: React.ReactNode;
}

const TableCell = ({ className, mono = false, muted = false, children, ...props }: ITableCellProps) => (
  <td
    className={cx(
      'px-3 py-1.5 align-middle',
      mono && 'font-mono',
      muted ? 'text-fg-secondary' : 'text-fg-primary',
      className
    )}
    {...props}
  >
    {children}
  </td>
);
TableCell.displayName = 'Table.Cell';

const Table = TableRoot as typeof TableRoot & {
  Header: typeof TableHeader;
  Body: typeof TableBody;
  Row: typeof TableRow;
  HeaderCell: typeof TableHeaderCell;
  SortableHeaderCell: typeof TableSortableHeaderCell;
  Cell: typeof TableCell;
};
Table.Header = TableHeader;
Table.Body = TableBody;
Table.Row = TableRow;
Table.HeaderCell = TableHeaderCell;
Table.SortableHeaderCell = TableSortableHeaderCell;
Table.Cell = TableCell;

export default Table;
